import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Hero from '../components/Hero';
import SEO from '../components/SEO';
import ContactBox from '../components/ContactBox';
import { pagesData, servicesData } from '../data';
import { 
  ShieldCheck, 
  HardHat, 
  Ruler, 
  Clock, 
  CheckCircle2, 
  ChevronRight, 
  ChevronDown, 
  Star, 
  MapPin, 
  Wrench, 
  Split, 
  Tv, 
  HelpCircle,
  FileCheck2,
  ArrowRight,
  Users,
  Truck,
  Phone,
  Mail
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { getOptimizedImageUrl, renderFormattedText } from '../lib/utils';
import { localBusinessSchema, websiteSchema } from '../lib/schema';

export default function Home() {
  const page = pagesData.index;
  const [openFaqIdx, setOpenFaqIdx] = useState(null);

  const introSection = page.sections.find(s => s.type === 'intro');
  const kloakserviceSection = page.sections.find(s => s.type === 'kloakservice');
  const galleryTeaserSection = page.sections.find(s => s.type === 'gallery_teaser');
  const fullServiceSection = page.sections.find(s => s.type === 'full_service');
  const localAreaSection = page.sections.find(s => s.type === 'local_area');
  const faqSection = page.sections.find(s => s.type === 'faq');
  const testimonialsSection = page.sections.find(s => s.type === 'testimonials');

  const schemaList = [
    websiteSchema,
    localBusinessSchema,
    ...(page.schema_markup ? [page.schema_markup] : [])
  ];

  return (
    <>
      <SEO 
        title={page.title} 
        description={page.meta.description}
        image={page.meta?.ogImage}
        schemaMarkup={schemaList}
      />
      <Hero />

      {/* Trust Bar */}
      <section className="bg-stone-100 py-10 border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
            <div className="flex flex-col items-center text-center gap-2.5">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 shadow-sm">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <span className="text-sm font-bold uppercase tracking-wider text-stone-900">Autoriseret Kloakmester</span>
              <span className="text-xs text-stone-500 font-medium">Over 20 års erfaring</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2.5">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 shadow-sm">
                <HardHat className="w-6 h-6" />
              </div>
              <span className="text-sm font-bold uppercase tracking-wider text-stone-900">Private & Erhverv</span>
              <span className="text-xs text-stone-500 font-medium">Små & store opgaver</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2.5">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 shadow-sm">
                <Ruler className="w-6 h-6" />
              </div>
              <span className="text-sm font-bold uppercase tracking-wider text-stone-900">Præcis Udførelse</span>
              <span className="text-xs text-stone-500 font-medium">Korrekt dokumentation</span>
            </div>
            <div className="flex flex-col items-center text-center gap-2.5">
              <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center text-orange-600 shadow-sm">
                <Clock className="w-6 h-6" />
              </div>
              <span className="text-sm font-bold uppercase tracking-wider text-stone-900">Overholder Aftaler</span>
              <span className="text-xs text-stone-500 font-medium">Klare priser & vilkår</span>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section - H1 & Primary Story */}
      {introSection && (
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800 mb-6">
                <ShieldCheck className="w-4 h-4 text-orange-600" />
                {introSection.badge || "Autoriseret kloakmester"}
              </span>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-stone-900 tracking-tight leading-tight mb-6">
                {introSection.h1}
              </h1>
              <p className="text-lg md:text-xl text-stone-600 leading-relaxed">
                {introSection.lead}
              </p>
            </div>

            {/* Sub-heading & Paragraphs Block */}
            <div className="bg-stone-50 rounded-2xl p-8 md:p-12 border border-stone-200/80 shadow-sm">
              <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-6">
                {introSection.h2}
              </h2>
              <div className="space-y-4 text-stone-600 text-base md:text-lg leading-relaxed">
                {introSection.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Services Grid Section */}
      <section className="py-24 bg-stone-50 border-t border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="text-stone-500 text-sm font-bold uppercase tracking-[0.3em] mb-4">Vores Ekspertise</p>
            <h2 className="text-3xl md:text-5xl font-bold text-stone-900 mb-6 tracking-tight">
              Kloakarbejde, entreprenørarbejde og anlæg i Østjylland
            </h2>
            <p className="text-stone-600 text-lg">
              Vi forener mange års erfaring inden for kloakarbejde med professionelt entreprenør- og anlægsarbejde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {servicesData.map((service) => {
              const Icon = Icons[service.icon_name] || Icons.Wrench;
              return (
                <div
                  key={service.id}
                  className="group bg-white border border-stone-200 rounded-2xl overflow-hidden hover:shadow-xl hover:shadow-stone-300/40 transition-all duration-300 flex flex-col h-full"
                >
                  <Link
                    to={`/${service.slug}`}
                    className="relative h-60 overflow-hidden bg-stone-900 block"
                    aria-label={service.title}
                  >
                    <img
                      src={getOptimizedImageUrl(service.image, 600)}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/70 via-stone-950/20 to-transparent" />
                    <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm p-3 rounded-xl shadow-md">
                      <Icon className="w-5 h-5 text-orange-600" />
                    </div>
                  </Link>
                  
                  <div className="p-7 flex flex-col flex-grow">
                    <h3 className="text-xl font-bold text-stone-900 mb-3 group-hover:text-orange-600 transition-colors">
                      <Link
                        to={`/${service.slug}`}
                        className="hover:text-orange-600 transition-colors inline-block"
                      >
                        {service.title}
                      </Link>
                    </h3>
                    <p className="text-stone-600 mb-6 flex-grow text-sm md:text-base leading-relaxed">
                      {service.short_description}
                    </p>
                    <Link
                      to={`/${service.slug}`}
                      className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-orange-600 hover:text-orange-700 transition-colors group/link mt-auto pt-4 border-t border-stone-100"
                    >
                      {service.cta_text || `Læs mere om ${service.title}`}
                      <ChevronRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Kloakservice & Kloakseparering Section */}
      {kloakserviceSection && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              
              {/* Left Column: Content */}
              <div className="lg:col-span-7 space-y-10">
                <div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800 mb-4">
                    <Wrench className="w-3.5 h-3.5" /> Kloakservice & Fejlfinding
                  </span>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 tracking-tight mb-6">
                    {kloakserviceSection.h2}
                  </h2>
                  <div className="space-y-4 text-stone-600 text-base md:text-lg leading-relaxed">
                    {kloakserviceSection.paragraphs.map((p, i) => (
                      <p key={i}>{renderFormattedText(p)}</p>
                    ))}
                  </div>
                </div>

                {/* Sub-section: Kloakseparering */}
                <div className="bg-stone-50 p-6 md:p-8 rounded-2xl border border-stone-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-orange-600 text-white flex items-center justify-center shrink-0">
                      <Split className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-stone-900">
                      {kloakserviceSection.h3}
                    </h3>
                  </div>
                  <div className="space-y-3 text-stone-600 text-base leading-relaxed">
                    {kloakserviceSection.separering_paragraphs.map((p, i) => (
                      <p key={i}>{renderFormattedText(p)}</p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: Visual Feature List */}
              <div className="lg:col-span-5 space-y-6">
                <div className="relative rounded-2xl overflow-hidden shadow-lg border border-stone-200 group">
                  <img
                    src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/20250517-070815450-ios.jpg", 800)}
                    alt="Inspektion og spuling af kloakledning og brønd"
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-transparent to-transparent flex items-end p-6">
                    <div>
                      <span className="text-orange-400 font-bold text-xs uppercase tracking-wider block mb-1">
                        Moderne Udstyr
                      </span>
                      <p className="text-white font-bold text-lg">
                        TV-inspektion & elektronisk kabelsøgning
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-stone-50 rounded-2xl p-6 border border-stone-200">
                  <p className="text-sm font-bold uppercase tracking-wider text-stone-900 mb-4 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-orange-600" />
                    Hvad vi hjælper med
                  </p>
                  <ul className="space-y-3">
                    {kloakserviceSection.list.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 text-stone-700 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-orange-600 shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Gallery Teaser Section (Preserved) */}
      {galleryTeaserSection && (
        <section className="py-24 bg-stone-900 text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-15">
            <img 
              src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/6015610c-faa3-4021-bc7e-a866f30ca512/7544e4c2-59ce-4207-b379-e1dab6e0007b.jpg", 1600)} 
              alt="Fuldført anlægsarbejde og nylagt flisebelægning ved bolig" 
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-orange-400 border border-white/15 mb-6 backdrop-blur-sm">
              <i className="fa-solid fa-images text-xs" /> Billeder fra projekter
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-6 tracking-tight">
              {galleryTeaserSection.title}
            </h2>
            <p className="text-stone-300 text-lg md:text-xl mb-10 max-w-2xl mx-auto leading-relaxed">
              {galleryTeaserSection.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to={galleryTeaserSection.ctaHref}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-orange-600 text-white px-8 py-4 rounded-xl text-base font-bold hover:bg-orange-700 transition-all shadow-xl shadow-orange-900/40 group"
              >
                {galleryTeaserSection.ctaText}
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/kontakt"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 rounded-xl text-base font-bold transition-all"
              >
                Få et tilbud
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Én entreprenør og kloakmester til hele opgaven */}
      {fullServiceSection && (
        <section className="py-24 bg-stone-50 border-b border-stone-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800 mb-4">
                <HardHat className="w-3.5 h-3.5" /> Totalentreprise & Sammenhæng
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-stone-900 tracking-tight mb-6">
                {fullServiceSection.h2}
              </h2>
              <div className="space-y-4 text-stone-600 text-base md:text-lg leading-relaxed">
                {fullServiceSection.paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
              {fullServiceSection.list.map((item, i) => (
                <div key={i} className="flex items-start gap-4 bg-white p-5 rounded-xl border border-stone-200 shadow-sm hover:border-orange-200 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0 text-orange-600 mt-0.5">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                  <span className="text-stone-800 font-medium leading-snug">{item}</span>
                </div>
              ))}
            </div>

            {fullServiceSection.outro && (
              <div className="bg-orange-50/70 border border-orange-200/80 rounded-2xl p-6 text-center max-w-3xl mx-auto">
                <p className="text-stone-800 font-medium leading-relaxed">
                  {fullServiceSection.outro}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Din lokale kloakmester i Galten og omegn */}
      {localAreaSection && (
        <section className="py-24 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              <div className="lg:col-span-7 space-y-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800">
                  <MapPin className="w-3.5 h-3.5 text-orange-600" /> Lokalt kloakfirma
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 tracking-tight">
                  {localAreaSection.h2}
                </h2>
                <div className="space-y-4 text-stone-600 text-base md:text-lg leading-relaxed">
                  {localAreaSection.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>

                <div className="pt-2">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3.5 rounded-xl font-bold transition-all shadow-md shadow-orange-900/20"
                  >
                    Kontakt os om din opgave
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Covered Cities Grid */}
              <div className="lg:col-span-5 bg-stone-50 p-8 rounded-3xl border border-stone-200 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-xl bg-orange-600 text-white flex items-center justify-center shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold text-stone-900 text-lg">Primære dækningsområder</h3>
                    <p className="text-xs text-stone-500">Kloakarbejde & entreprenør i Østjylland</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {localAreaSection.cities.map((city, i) => (
                    <div 
                      key={i} 
                      className={`p-4 rounded-xl border transition-all ${
                        city.name === 'Galten' 
                          ? 'bg-orange-50 border-orange-300 shadow-sm' 
                          : 'bg-white border-stone-200 hover:border-orange-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-bold text-stone-900">{city.name}</span>
                        <MapPin className={`w-4 h-4 ${city.name === 'Galten' ? 'text-orange-600' : 'text-stone-400'}`} />
                      </div>
                      <span className="text-[11px] font-medium text-stone-500 uppercase tracking-wider block">
                        {city.tag}
                      </span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-stone-500 text-center mt-6">
                  Vi dækker naturligvis også de omkringliggende byer og landdistrikter.
                </p>
              </div>

            </div>
          </div>
        </section>
      )}





      {/* VISUAL BREAK / BRAND BANNER */}
      <section className="relative overflow-hidden bg-stone-100">
        <div className="relative w-full h-[380px] sm:h-[460px] md:h-[540px] lg:h-[620px]">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1800,quality=85,format=auto/assets/6015610c-faa3-4021-bc7e-a866f30ca512/c48333a8-7cb3-4126-b759-a84656402d0b.jpg"
            alt="Entreprenør Jan Hedegaard - professionelt kloak- og gravearbejde i Galten og Østjylland"
            className="w-full h-full object-cover object-center"
          />
        </div>
      </section>

      {/* FAQ Section */}
      {faqSection && faqSection.display_on_page && faqSection.items && faqSection.items.length > 0 && (
        <section className="py-24 bg-stone-50 border-t border-b border-stone-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-orange-100 text-orange-800 mb-4">
                <HelpCircle className="w-3.5 h-3.5" /> Spørgsmål & Svar
              </span>
              <h2 className="text-3xl md:text-4xl font-extrabold text-stone-900 tracking-tight mb-4">
                {faqSection.section_heading?.text || "Ofte stillede spørgsmål om kloakarbejde og priser"}
              </h2>
              <p className="text-stone-600">
                Få svar på de mest almindelige spørgsmål om kloakmesterarbejde, kloakservice og priser.
              </p>
            </div>

            <div className="space-y-4">
              {faqSection.items.map((item, idx) => {
                const isOpen = openFaqIdx === idx;
                return (
                  <div
                    key={idx}
                    className={`border rounded-2xl overflow-hidden transition-all duration-300 ${
                      isOpen
                        ? 'border-orange-200 bg-white shadow-md'
                        : 'border-stone-200 bg-white hover:border-stone-300'
                    }`}
                  >
                    <h3 className="m-0 p-0 text-inherit font-inherit">
                      <button
                        type="button"
                        onClick={() => setOpenFaqIdx(isOpen ? null : idx)}
                        className="w-full flex items-center justify-between p-6 text-left font-bold text-base md:text-lg text-stone-900 hover:text-orange-600 transition-colors focus:outline-none cursor-pointer"
                        aria-expanded={isOpen}
                      >
                        <span className="pr-4">{item.question}</span>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                          isOpen ? 'bg-orange-100 text-orange-600' : 'bg-stone-100 text-stone-500'
                        }`}>
                          <ChevronDown
                            className={`w-5 h-5 transition-transform duration-300 ${
                              isOpen ? 'transform rotate-180 text-orange-600' : ''
                            }`}
                          />
                        </div>
                      </button>
                    </h3>

                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: "easeInOut" }}
                        >
                          <div className="px-6 pb-6 text-stone-600 text-base leading-relaxed border-t border-stone-100 pt-4">
                            <p>{item.answer}</p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section (Preserved) */}
      {testimonialsSection && (
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <h2 className="text-stone-500 text-sm font-bold uppercase tracking-[0.3em] mb-4">Anmeldelser</h2>
              <h3 className="text-3xl md:text-4xl font-bold text-stone-900 tracking-tight mb-4">
                {testimonialsSection.title}
              </h3>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-stone-50 border border-stone-200 text-stone-700 text-sm">
                <span>⭐</span>
                <span>
                  <strong className="text-stone-900 font-semibold">5 / 5 stjerner</strong> baseret på anmeldelser på{' '}
                  <a
                    href="https://www.3byggetilbud.dk/leverandoer/entreprenr-jan-hedegaard-aps/"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-orange-600 font-semibold underline hover:text-orange-700 transition-colors"
                  >
                    3byggetilbud.dk
                  </a>
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonialsSection.items.map((item, i) => (
                <div key={i} className="bg-stone-50 p-8 rounded-2xl border border-stone-200 flex flex-col justify-between shadow-sm hover:shadow-md transition-shadow">
                  <div>
                    <div className="flex gap-1 mb-6">
                      {[...Array(5)].map((_, idx) => (
                        <Star key={idx} className="w-5 h-5 fill-orange-500 text-orange-500" />
                      ))}
                    </div>
                    <p className="text-stone-600 italic leading-relaxed mb-8">
                      "{item.quote}"
                    </p>
                  </div>
                  <div className="pt-4 border-t border-stone-200/80">
                    <div className="mb-3">
                      <p className="font-bold text-stone-900">{item.author}</p>
                      <span className="text-stone-500 text-sm">{item.location}</span>
                    </div>
                    <div className="inline-flex items-center gap-1.5 text-xs text-stone-600 bg-white px-3 py-1.5 rounded-lg border border-stone-200 shadow-2xs">
                      <ShieldCheck className="w-3.5 h-3.5 text-orange-600 shrink-0" />
                      <span>
                        Verificeret 5-stjernet anmeldelse via{' '}
                        <a
                          href="https://www.3byggetilbud.dk/leverandoer/entreprenr-jan-hedegaard-aps/"
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="font-medium text-stone-900 underline hover:text-orange-600"
                        >
                          3byggetilbud.dk
                        </a>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section - "Skal vi se nærmere på din opgave" */}
      <section className="py-24 bg-stone-50 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ContactBox 
            title="Skal vi se nærmere på din opgave?"
            subtitle="Kontakt Entreprenør Jan Hedegaard, hvis du har brug for en autoriseret kloakmester eller ønsker rådgivning om kloakservice, kloakseparering og andet kloakarbejde. Vi tager en grundig samtale om opgaven og giver gerne et uforpligtende tilbud på den rette løsning."
          />
        </div>
      </section>
    </>
  );
}
